import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from '../../hooks/use-toast';
import { BACKEND_URL } from '../../config/canonical';
import { Plus, Lightbulb, Trash2, Eye, Pencil } from 'lucide-react';
import { RISK_COLORS, LEAD_STATUS_COLORS } from './constants';

/**
 * LeadsTab — full-page "Add Lead" flow now lives at
 * `/cases/:caseId/leads/new` (LeadFormPage). Dialog removed.
 */
const LeadsTab = ({
  caseId,
  leads,
  canEdit,
  isAdmin,
  token,
  onRefresh,
  onViewItem,
  onEditItem,
  onDeleteItem,
}) => {
  const navigate = useNavigate();

  const handleDeleteLead = async (leadId) => {
    if (onDeleteItem) return onDeleteItem('lead', leadId, 'Delete this lead?');
    if (!window.confirm('Delete this lead?')) return;
    try {
      await fetch(`${BACKEND_URL}/api/leads/${leadId}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      onRefresh();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-4">
      {canEdit && (
        <div className="flex justify-end">
          <Button className="bg-[#0F172A] hover:bg-[#1E293B] text-white"
            onClick={() => navigate(`/cases/${caseId}/leads/new`)} data-testid="add-lead-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Lead
          </Button>
        </div>
      )}
      {leads.length === 0 ? (
        <Card className="bg-white border-slate-200">
          <CardContent className="p-8 text-center">
            <Lightbulb className="w-12 h-12 mx-auto text-slate-600 mb-2" />
            <p className="text-slate-500">No leads yet</p>
          </CardContent>
        </Card>
      ) : (
        leads.map(lead => (
          <Card key={lead.id} className="bg-white border-slate-200 hover:border-[#0F172A]/30 transition-colors" data-testid={`lead-card-${lead.id}`}>
            <CardContent className="p-4">
              <button onClick={() => onViewItem(lead, 'lead')} className="text-left w-full">
                <div className="flex items-start justify-between mb-2 gap-2 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={`${LEAD_STATUS_COLORS[lead.status] || 'bg-blue-500'} text-white text-xs`}>{lead.status}</Badge>
                    <Badge className={`${RISK_COLORS[lead.priority] || 'bg-yellow-500'} text-white text-xs`}>{lead.priority}</Badge>
                    {lead.lead_source && <span className="text-slate-500 text-xs">Source: {lead.lead_source}</span>}
                  </div>
                </div>
                <p className="text-slate-800 text-sm">{lead.description}</p>
                {lead.notes && <p className="text-slate-500 text-xs mt-2">{lead.notes}</p>}
                <p className="text-xs text-slate-500 mt-2">Received: {lead.received_date}</p>
              </button>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-100"
                  onClick={() => onViewItem(lead, 'lead')} data-testid={`lead-view-${lead.id}`} title="View details">
                  <Eye className="w-4 h-4 mr-1" /> View
                </Button>
                {canEdit && (
                  <Button size="sm" variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-100"
                    onClick={() => (onEditItem ? onEditItem(lead, 'lead') : onViewItem(lead, 'lead'))}
                    data-testid={`lead-edit-${lead.id}`} title="Edit lead">
                    <Pencil className="w-4 h-4 mr-1" /> Edit
                  </Button>
                )}
                {isAdmin && (
                  <Button size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50"
                    onClick={() => handleDeleteLead(lead.id)} data-testid={`lead-delete-${lead.id}`} title="Delete lead">
                    <Trash2 className="w-4 h-4 mr-1" /> Delete
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
};

export default LeadsTab;
